import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar as CalendarIcon, 
  Users,
  CheckCircle,
  XCircle,
  Briefcase
} from 'lucide-react';
import { crewCalendarAPI, crewAPI } from '../services/api';

const CrewCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [dailySummary, setDailySummary] = useState([]);
  const [crewList, setCrewList] = useState([]);
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedCrew, setSelectedCrew] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  const roles = useMemo(() => {
    const roleList = [...new Set(crewList.map(c => c.role))];
    return roleList.sort();
  }, [crewList]);

  const filteredCrew = useMemo(() => {
    if (selectedRole === 'all') return crewList;
    return crewList.filter(c => c.role === selectedRole);
  }, [crewList, selectedRole]);

  const dateRange = useMemo(() => {
    const start = new Date(currentDate);
    const end = new Date(currentDate);
    
    start.setDate(1);
    end.setMonth(end.getMonth() + 1);
    end.setDate(0);
    
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  }, [currentDate]);

  useEffect(() => {
    fetchData();
  }, [dateRange, selectedRole, selectedCrew]);

  useEffect(() => {
    fetchCrewList();
  }, []);

  const fetchCrewList = async () => {
    try {
      const res = await crewAPI.getAll();
      setCrewList(res.data);
    } catch (error) {
      console.error('Fout bij ophalen personeel:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await crewCalendarAPI.getDailySummary(
        dateRange.start,
        dateRange.end,
        selectedCrew !== 'all' ? selectedCrew : undefined,
        selectedRole !== 'all' ? selectedRole : undefined
      );
      setDailySummary(res.data.daily_summary || []);
    } catch (error) {
      console.error('Fout bij ophalen kalendergegevens:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
    setSelectedDay(null);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDay(null);
  };

  const calendarDays = useMemo(() => {
    const days = [];
    const start = new Date(currentDate);
    start.setDate(1);
    
    const firstDay = start.getDay();
    
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    
    const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
    for (let i = 1; i <= lastDay; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), i);
      days.push(date.toISOString().split('T')[0]);
    }
    
    return days;
  }, [currentDate]);

  const getDaySummary = (dateStr) => {
    return dailySummary.find(d => d.date === dateStr);
  };

  const getDayUtilization = (dateStr) => {
    const summary = getDaySummary(dateStr);
    if (!summary) return { total: 0, booked: 0, percentage: 0 };
    
    let totalCrew = 0;
    let bookedCrew = 0;
    
    Object.values(summary.crew).forEach(c => {
      totalCrew += 1;
      if (c.is_booked) bookedCrew += 1;
    });
    
    return {
      total: totalCrew,
      booked: bookedCrew,
      percentage: totalCrew > 0 ? Math.round((bookedCrew / totalCrew) * 100) : 0
    };
  };

  const getUtilizationColor = (percentage) => {
    if (percentage === 0) return 'bg-green-50 border-green-200';
    if (percentage < 30) return 'bg-green-100 border-green-300';
    if (percentage < 60) return 'bg-yellow-100 border-yellow-300';
    if (percentage < 90) return 'bg-orange-100 border-orange-300';
    return 'bg-red-100 border-red-300';
  };

  const monthNames = ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni',
    'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];

  const dayNames = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'];

  const isToday = (dateStr) => {
    return dateStr === new Date().toISOString().split('T')[0];
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Personeel Beschikbaarheid</h1>
          <p className="text-gray-500 mt-1">Visueel overzicht van personeel planning en boekingen</p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => navigateMonth(-1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <h2 className="text-xl font-semibold min-w-[200px] text-center">
                  {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                </h2>
                <Button variant="outline" size="icon" onClick={() => navigateMonth(1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <Button variant="outline" onClick={goToToday}>
                Vandaag
              </Button>
            </div>
            
            <div className="flex items-center gap-4">
              <Select value={selectedRole} onValueChange={(v) => { setSelectedRole(v); setSelectedCrew('all'); }}>
                <SelectTrigger className="w-[180px]" data-testid="role-filter">
                  <SelectValue placeholder="Alle Functies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Functies</SelectItem>
                  {roles.map(role => (
                    <SelectItem key={role} value={role}>{role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={selectedCrew} onValueChange={setSelectedCrew}>
                <SelectTrigger className="w-[200px]" data-testid="crew-filter">
                  <SelectValue placeholder="Alle Personeel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Personeel</SelectItem>
                  {filteredCrew.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-6 text-sm">
        <span className="font-medium">Personeel Ingepland:</span>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-green-50 border-green-200 rounded border"></div>
          <span>0%</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-green-100 border-green-300 rounded border"></div>
          <span>1-30%</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-yellow-100 border-yellow-300 rounded border"></div>
          <span>30-60%</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-orange-100 border-orange-300 rounded border"></div>
          <span>60-90%</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 bg-red-100 border-red-300 rounded border"></div>
          <span>90-100%</span>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center h-96">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {dayNames.map(day => (
                <div key={day} className="text-center font-medium text-gray-500 py-2">
                  {day}
                </div>
              ))}
              
              {calendarDays.map((dateStr, index) => {
                if (!dateStr) {
                  return <div key={`empty-${index}`} className="h-24 bg-gray-50 rounded"></div>;
                }
                
                const utilization = getDayUtilization(dateStr);
                const dayNum = new Date(dateStr).getDate();
                const isSelected = selectedDay === dateStr;
                
                return (
                  <div
                    key={dateStr}
                    className={`h-24 rounded border cursor-pointer transition-all hover:shadow-md ${
                      getUtilizationColor(utilization.percentage)
                    } ${isToday(dateStr) ? 'ring-2 ring-purple-500' : ''} ${
                      isSelected ? 'ring-2 ring-blue-500 shadow-lg' : ''
                    }`}
                    onClick={() => setSelectedDay(isSelected ? null : dateStr)}
                    data-testid={`calendar-day-${dateStr}`}
                  >
                    <div className="p-2 h-full flex flex-col">
                      <div className="flex justify-between items-start">
                        <span className={`text-sm font-medium ${isToday(dateStr) ? 'text-purple-600' : ''}`}>
                          {dayNum}
                        </span>
                        {utilization.booked > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {utilization.booked}/{utilization.total}
                          </Badge>
                        )}
                      </div>
                      {utilization.total > 0 && (
                        <div className="mt-auto">
                          <div className="flex items-center gap-1 text-xs text-gray-600">
                            <Users className="w-3 h-3" />
                            <span>{utilization.total - utilization.booked} vrij</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {selectedDay && (
        <Card className="border-purple-200 bg-purple-50/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-purple-600" />
              {new Date(selectedDay).toLocaleDateString('nl-NL', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const summary = getDaySummary(selectedDay);
              if (!summary) return <p className="text-gray-500">Geen gegevens beschikbaar</p>;
              
              const crewEntries = Object.entries(summary.crew);
              const availableCrew = crewEntries.filter(([_, c]) => !c.is_booked);
              const bookedCrew = crewEntries.filter(([_, c]) => c.is_booked);
              
              return (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-semibold text-green-700 mb-3 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Beschikbaar ({availableCrew.length})
                    </h3>
                    {availableCrew.length === 0 ? (
                      <p className="text-sm text-gray-500">Geen personeel beschikbaar</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {availableCrew.map(([crewId, crewData]) => (
                          <div
                            key={crewId}
                            className="p-3 rounded-lg bg-green-50 border border-green-200"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-medium text-gray-900">{crewData.name}</h4>
                                <p className="text-xs text-gray-500">{crewData.role}</p>
                              </div>
                              <CheckCircle className="w-5 h-5 text-green-500" />
                            </div>
                            <p className="text-sm text-green-600 mt-1">€{crewData.ratePerDay}/dag</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-semibold text-orange-700 mb-3 flex items-center gap-2">
                      <Briefcase className="w-4 h-4" />
                      Ingepland ({bookedCrew.length})
                    </h3>
                    {bookedCrew.length === 0 ? (
                      <p className="text-sm text-gray-500">Geen personeel ingepland</p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {bookedCrew.map(([crewId, crewData]) => (
                          <div
                            key={crewId}
                            className="p-3 rounded-lg bg-orange-50 border border-orange-200"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-medium text-gray-900">{crewData.name}</h4>
                                <p className="text-xs text-gray-500">{crewData.role}</p>
                              </div>
                              <XCircle className="w-5 h-5 text-orange-500" />
                            </div>
                            {crewData.project && (
                              <div className="mt-2 text-xs">
                                <p className="font-medium text-gray-700">{crewData.project.name}</p>
                                <p className="text-gray-500">{crewData.project.customer}</p>
                                {crewData.project.location && (
                                  <p className="text-gray-400">{crewData.project.location}</p>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CrewCalendar;
